import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getHeroList, getGwDefense, saveGwDefense } from '../../api/senaApi';
import { heroPortrait, skillImg as senaSkillImg, skillLabel } from './senaAssets';
import './SenaGuideWrite.css';
import './SenaGwCounterWrite.css';

const MAX_SKILLS = 3;

const skillImg = (heroNm, skillNo) => senaSkillImg(heroNm, 'ALLY', skillNo);

// 길드전 방어 덱 등록/수정 — 영웅 3명 + 영웅별 비고 + 스킬 순서 3개
export default function SenaGwDefenseWrite() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const defenseId = params.get('id');
  const userId = localStorage.getItem('userId') || '게스트';

  const [allyHeroes, setAllyHeroes] = useState([]);
  const [selected, setSelected] = useState([null, null, null]);
  const [searches, setSearches] = useState(['', '', '']);
  const [notes, setNotes] = useState(['', '', '']);
  const [skillIds, setSkillIds] = useState([]);   // 클릭 순서대로 최대 3개
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getHeroList('ALLY').then(r => {
      const heroes = r.data || [];
      setAllyHeroes(heroes);

      if (defenseId) {
        getGwDefense(Number(defenseId)).then(res => {
          const d = res.data;
          if (!d) return;
          setSelected([
            heroes.find(h => h.heroId === d.hero1Id) || null,
            heroes.find(h => h.heroId === d.hero2Id) || null,
            heroes.find(h => h.heroId === d.hero3Id) || null,
          ]);
          setNotes([d.note1 || '', d.note2 || '', d.note3 || '']);
          setSkillIds((d.skillIds || '').split(',').filter(Boolean));
        }).catch(() => {});
      }
    }).catch(() => {});
  }, [defenseId]);

  const handleSelect = (slotIdx, hero) => {
    setSelected(prev => { const next = [...prev]; next[slotIdx] = hero; return next; });
    setSearches(prev => { const next = [...prev]; next[slotIdx] = ''; return next; });
  };

  const handleRemove = (slotIdx) => {
    const removed = selected[slotIdx];
    setSelected(prev => { const next = [...prev]; next[slotIdx] = null; return next; });
    // 빠진 영웅의 스킬은 순서에서도 제거
    if (removed) {
      const ids = (removed.skills || []).map(s => String(s.skillId));
      setSkillIds(prev => prev.filter(id => !ids.includes(id)));
    }
  };

  const toggleSkill = (skillId) => {
    const strId = String(skillId);
    setSkillIds(prev => {
      if (prev.includes(strId)) return prev.filter(id => id !== strId);
      if (prev.length >= MAX_SKILLS) return prev;
      return [...prev, strId];
    });
  };

  const skillOrder = (skillId) => {
    const idx = skillIds.indexOf(String(skillId));
    return idx >= 0 ? idx + 1 : 0;
  };

  const handleSave = async () => {
    if (selected.filter(Boolean).length < 3) {
      alert('영웅 3명을 모두 선택해주세요.');
      return;
    }
    setSaving(true);
    try {
      await saveGwDefense({
        defenseId: defenseId ? Number(defenseId) : undefined,
        hero1Id: selected[0].heroId,
        hero2Id: selected[1].heroId,
        hero3Id: selected[2].heroId,
        note1: notes[0] || null,
        note2: notes[1] || null,
        note3: notes[2] || null,
        skillIds: skillIds.length > 0 ? skillIds.join(',') : null,
        authorNm: userId,
      });
      alert('저장되었습니다.');
      navigate('/sena/guild-war-def');
    } catch (e) {
      alert(e?.response?.data || '저장에 실패했습니다.');
    } finally { setSaving(false); }
  };

  const pickedHeroes = selected.filter(Boolean);

  return (
    <div className="sena-page">
      <header className="sena-header">
        <button className="siege-back-btn" onClick={() => navigate(-1)}>← 뒤로</button>
        <button className="siege-home-btn" onClick={() => navigate('/sena')}>홈</button>
        <h1 className="sena-header-guild">{defenseId ? '방어 덱 수정' : '방어 덱 등록'}</h1>
        <p className="sena-header-sub">길드전(방어)</p>
      </header>

      <div className="gw-container">
        {/* 영웅 3명 */}
        <section className="gw-section">
          <h3 className="gw-section-title">방어 영웅 3명</h3>
          <div className="gw-ally-slots">
            {selected.map((hero, idx) => (
              <div key={idx} className={`gw-ally-slot ${hero ? 'filled' : ''}`}>
                {hero ? (
                  <>
                    <span className="gw-ally-name">{hero.heroNm}</span>
                    <button className="gw-ally-remove" onClick={() => handleRemove(idx)}>×</button>
                  </>
                ) : <span className="gw-ally-empty">슬롯 {idx + 1}</span>}
              </div>
            ))}
          </div>

          {/* 영웅별 비고 */}
          {selected.some(Boolean) && (
            <div className="gwc-note-list">
              <p className="gw-hint">영웅별 비고 (세트옵션·치확·약확·속공 등 세팅 메모)</p>
              {selected.map((hero, idx) => hero && (
                <div key={idx} className="gwc-note-row">
                  <span className="gwc-note-hero">
                    {heroPortrait(hero.heroNm, 'ALLY') && (
                      <img className="gwc-note-portrait" src={heroPortrait(hero.heroNm, 'ALLY')} alt="" />
                    )}
                    {hero.heroNm}
                  </span>
                  <input className="gw-input gwc-note-input" maxLength={200}
                    placeholder="예) 복수자 세트 / 치확 80 / 속공 2순위"
                    value={notes[idx]}
                    onChange={e => setNotes(prev => { const next = [...prev]; next[idx] = e.target.value; return next; })} />
                </div>
              ))}
            </div>
          )}

          {selected.map((hero, idx) => !hero && (
            <div key={idx} className="gw-hero-search-wrap" style={{ marginBottom: 10 }}>
              <input className="gw-hero-search" placeholder={`슬롯 ${idx + 1} 영웅 검색...`}
                value={searches[idx]}
                onChange={e => { const next = [...searches]; next[idx] = e.target.value; setSearches(next); }} />
              {searches[idx].length > 0 && (
                <div className="gw-hero-dropdown">
                  {allyHeroes.filter(h => h.heroNm.includes(searches[idx]))
                    .filter(h => !selected.some(s => s && s.heroId === h.heroId))
                    .map(h => (
                      <button key={h.heroId} className="gw-hero-dropdown-item" onClick={() => handleSelect(idx, h)}>
                        {h.heroNm}
                      </button>
                    ))}
                </div>
              )}
            </div>
          ))}
        </section>

        {/* 스킬 순서 */}
        {pickedHeroes.length > 0 && (
          <section className="gw-section">
            <h3 className="gw-section-title">스킬 순서 (클릭 순서대로, 최대 {MAX_SKILLS}개)</h3>
            <div className="gwc-skill-btns">
              {pickedHeroes.flatMap(hero => (hero.skills || []).map(sk => {
                const order = skillOrder(sk.skillId);
                const img = skillImg(hero.heroNm, sk.skillNo);
                return (
                  <button key={sk.skillId}
                    className={`gwc-skill-btn ${order > 0 ? 'ally selected' : ''}`}
                    onClick={() => toggleSkill(sk.skillId)}>
                    {order > 0 && <span className="gwc-skill-order">{order}</span>}
                    {img && <img className="gwc-skill-btn-img" src={img} alt="" />}
                    <span className="gwc-skill-btn-text">{hero.heroNm} {skillLabel(sk.skillNo)}</span>
                  </button>
                );
              }))}
            </div>
          </section>
        )}

        <div className="gw-footer">
          <button className="gw-btn" onClick={() => navigate(-1)}>취소</button>
          <button className="gw-btn primary" onClick={handleSave} disabled={saving}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
